import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useSession } from '@/hooks/use-session';
import { format, isToday, isYesterday, subDays, subWeeks, subMonths } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchChatThreads, ChatThread } from '@/utils/api';

// Group chat threads by date periods
function groupChatsByDate(threads: ChatThread[]) {
  const today = new Date();
  const yesterday = subDays(today, 1);
  const lastWeek = subWeeks(today, 1);
  const lastMonth = subMonths(today, 1);

  return {
    today: threads.filter(thread => isToday(new Date(thread.created_at))),
    yesterday: threads.filter(thread => isYesterday(new Date(thread.created_at))),
    lastWeek: threads.filter(thread => {
      const date = new Date(thread.created_at);
      return !isToday(date) && !isYesterday(date) && date > lastWeek;
    }),
    lastMonth: threads.filter(thread => {
      const date = new Date(thread.created_at);
      return date <= lastWeek && date > lastMonth;
    }),
    older: threads.filter(thread => {
      const date = new Date(thread.created_at);
      return date <= lastMonth;
    })
  };
}

export function ChatHistory() {
  const { session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  
  // Extract conversationId from URL if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const conversationId = params.get('conversationId');
    if (conversationId) {
      setSelectedId(conversationId);
    }
  }, [location.search]);
  
  // Query to fetch chat history
  const { 
    data: chats = [], 
    isLoading, 
    error,
    refetch 
  } = useQuery<ChatThread[]>({
    queryKey: ['chat-history', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return [];
      
      const { threads, error } = await fetchChatThreads(session.user.id);
      if (error) throw error;
      return threads;
    },
    enabled: !!session?.user?.id,
    staleTime: 60 * 1000, // 1 minute
  });
  
  // Set up real-time subscription
  useEffect(() => {
    if (!session?.user?.id) return;
    
    const channel = supabase.channel('chat-history-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_history', filter: `user_id=eq.${session.user.id}` },
        () => {
          // Invalidate and refetch when data changes
          queryClient.invalidateQueries({ queryKey: ['chat-history', session.user.id] });
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [session?.user?.id, queryClient]);
  
  // Group chats by date
  const groupedChats = groupChatsByDate(chats);
  
  // Handle chat item click
  const handleChatClick = (chatId: string) => {
    setSelectedId(chatId);
    navigate(`/chat?conversationId=${chatId}`);
  };
  
  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-md bg-indigo-950/30" />
        ))}
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 text-center space-y-3">
        <p className="text-red-400">Failed to load chat history</p>
        <Button 
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="flex items-center gap-2"
        >
          <RefreshCcw className="h-3 w-3" />
          Retry
        </Button>
      </div>
    );
  }
  
  if (chats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center space-y-3">
        <MessageCircle className="h-10 w-10 text-indigo-400/50" />
        <p className="text-white/70">No conversations yet</p>
        <p className="text-sm text-white/50">Start chatting to create history</p>
      </div>
    );
  }
  
  return (
    <div className="py-2 px-1">
      {/* Today */}
      {groupedChats.today.length > 0 && (
        <div className="mb-4">
          <h3 className="px-3 py-2 text-xs font-medium text-indigo-300/60">Today</h3>
          <div className="space-y-1 px-1">
            {groupedChats.today.map(chat => (
              <ChatHistoryItem 
                key={chat.id}
                chat={chat}
                isSelected={chat.id === selectedId}
                onClick={() => handleChatClick(chat.id)}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Yesterday */}
      {groupedChats.yesterday.length > 0 && (
        <div className="mb-4">
          <h3 className="px-3 py-2 text-xs font-medium text-indigo-300/60">Yesterday</h3>
          <div className="space-y-1 px-1">
            {groupedChats.yesterday.map(chat => (
              <ChatHistoryItem 
                key={chat.id}
                chat={chat}
                isSelected={chat.id === selectedId}
                onClick={() => handleChatClick(chat.id)}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Last 7 Days */}
      {groupedChats.lastWeek.length > 0 && (
        <div className="mb-4">
          <h3 className="px-3 py-2 text-xs font-medium text-indigo-300/60">Previous 7 Days</h3>
          <div className="space-y-1 px-1">
            {groupedChats.lastWeek.map(chat => (
              <ChatHistoryItem 
                key={chat.id}
                chat={chat}
                isSelected={chat.id === selectedId}
                onClick={() => handleChatClick(chat.id)}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Last 30 Days */}
      {groupedChats.lastMonth.length > 0 && (
        <div className="mb-4">
          <h3 className="px-3 py-2 text-xs font-medium text-indigo-300/60">Previous 30 Days</h3>
          <div className="space-y-1 px-1">
            {groupedChats.lastMonth.map(chat => (
              <ChatHistoryItem 
                key={chat.id}
                chat={chat}
                isSelected={chat.id === selectedId}
                onClick={() => handleChatClick(chat.id)}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Older */}
      {groupedChats.older.length > 0 && (
        <div className="mb-4">
          <h3 className="px-3 py-2 text-xs font-medium text-indigo-300/60">Older</h3>
          <div className="space-y-1 px-1">
            {groupedChats.older.map(chat => (
              <ChatHistoryItem 
                key={chat.id}
                chat={chat}
                isSelected={chat.id === selectedId}
                onClick={() => handleChatClick(chat.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface ChatHistoryItemProps {
  chat: ChatThread;
  isSelected: boolean;
  onClick: () => void;
}

function ChatHistoryItem({ chat, isSelected, onClick }: ChatHistoryItemProps) {
  return (
    <div className="relative group">
      <button
        className={cn(
          "w-full text-left px-3 py-2 rounded-md transition-colors duration-200 relative",
          isSelected 
            ? "bg-indigo-600/30 text-white" 
            : "hover:bg-indigo-600/10 text-white/80 hover:text-white"
        )}
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-sm font-medium truncate">
              {chat.title}
            </span>
            <span className="text-xs opacity-70 mt-0.5">
              {format(new Date(chat.created_at), 'h:mm a')}
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}

// Helper function for className conditionals
function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}
